// Карманные деньги детей: кошелёк, регулярные карманные, копилки и советы по финансовой грамотности.
import { z } from "zod";
import type { GoalDto } from "./dashboard.ts";
import type { Language } from "./enums.ts";

/** give — выдали родители (расход семьи «Дети»), gift — подарили, spend — потратил, save — копилка. */
export type PocketEntryKind = "give" | "gift" | "spend" | "save";

export interface PocketEntryDto {
  id: string;
  kind: PocketEntryKind;
  /** Изменение кошелька, сум: + пришло (выдали, подарили, взял из копилки), − ушло (потратил, отложил). */
  amount: number;
  note: string | null;
  /** Копилка — у записей «в копилку» / «из копилки». */
  goalName: string | null;
  /** YYYY-MM-DD по Ташкенту. */
  date: string;
  authorName: string;
  /** Записал сам ребёнок (для взрослого — пометка «сам»). */
  byChild: boolean;
  /** Можно удалить: «выдали» — взрослый; «потратил», «подарили» — ребёнок или взрослый; копилка — нет (снять обратно). */
  canDelete: boolean;
}

export interface PocketAllowanceDto {
  amount: number;
  /** 1 — понедельник … 7 — воскресенье. */
  weekday: number;
  /** Следующее начисление, YYYY-MM-DD. */
  nextDate: string;
}

export interface PocketWalletDto {
  childId: string;
  name: string;
  /** В кошельке сейчас, сум. */
  balance: number;
  allowance: PocketAllowanceDto | null;
  /** Этот месяц: получил (выдали + подарили), потратил, отложил (за вычетом снятого). */
  month: { got: number; spent: number; saved: number };
  /** Сколько в среднем откладывал в неделю за последние 4 недели, сум (не меньше 0). */
  savedPerWeek: number;
  /** Копилки ребёнка. */
  goals: GoalDto[];
  /** Последние записи, новые сверху. */
  entries: PocketEntryDto[];
}

/** Ребёнок в списке у взрослого. */
export interface PocketChildDto {
  childId: string;
  name: string;
  balance: number;
  allowance: PocketAllowanceDto | null;
}

const amount = z.number().positive().max(1e9);
const note = z.string().trim().max(100).nullable().default(null);

/** Ребёнок (или взрослый за него) записывает трату или подарок. */
export const PocketEntrySchema = z.object({ kind: z.enum(["gift", "spend"]), amount, note });

/** Взрослый выдаёт карманные сейчас — из семейного бюджета. */
export const PocketGiveSchema = z.object({ amount, note });

/** В копилку (amount > 0) или из копилки в кошелёк (amount < 0). */
export const PocketSaveSchema = z.object({
  goalId: z.string().uuid(),
  amount: z.number().refine((v) => v !== 0 && Math.abs(v) <= 1e9),
});

/** Регулярные карманные; amount null — выключить. */
export const PocketAllowanceSchema = z.object({
  amount: amount.nullable(),
  weekday: z.number().int().min(1).max(7),
});

/** Как запись меняет кошелёк: траты и «в копилку» уменьшают, остальное — пополняет. */
export function walletDelta(kind: PocketEntryKind, amount: number): number {
  return kind === "spend" || kind === "save" ? -amount : amount;
}

/** Сколько предложить откладывать в неделю, пока ребёнок не копил сам: пятая часть карманных, кратно 1 000 сум. */
export function suggestedWeeklySave(allowance: number | null): number | null {
  if (!allowance || allowance <= 0) return null;
  const fifth = Math.round(allowance / 5 / 1000) * 1000;
  return fifth > 0 ? fifth : null;
}

/** Дальше года ребёнку считать недели незачем — подсказка не мотивирует. */
const MAX_PACE_WEEKS = 52;

/** За сколько недель накопится остаток, если откладывать perWeek. null — откладывать нечего или дольше года. */
export function weeksToGoal(left: number, perWeek: number | null): number | null {
  if (!perWeek || perWeek <= 0 || left <= 0) return null;
  const weeks = Math.ceil(left / perWeek);
  return weeks <= MAX_PACE_WEEKS ? weeks : null;
}

/** Темп для подсказки у копилки: сколько ребёнок реально откладывает, иначе — предложение от карманных. */
export function weeklyPace(wallet: { savedPerWeek: number; allowance: { amount: number } | null }): number | null {
  return wallet.savedPerWeek > 0 ? wallet.savedPerWeek : suggestedWeeklySave(wallet.allowance?.amount ?? null);
}

/** Советы для детей: один на неделю, на «ты», без морали — по делу. */
export const POCKET_TIPS: Record<Language, string[]> = {
  ru: [
    "Сначала отложи, потом трать: как только пришли карманные — сразу положи часть в копилку.",
    "Хочется что-то купить? Подожди один день. Если всё ещё хочется — значит, правда нужно.",
    "Записывай траты — так видно, куда уходят деньги.",
    "Маленькие покупки складываются: 5 000 каждый день — это 150 000 за месяц.",
    "Копить легче, когда понятно на что: придумай цель и узнай, сколько она стоит.",
    "Сравни цены: в другом магазине то же самое может стоить дешевле.",
    "Нужно или хочется? Без нужного не обойтись, а хочется — можно и подождать.",
    "Взял из копилки — подумай, как вернуть. Копилка растёт, когда в неё кладут.",
    "Подарили деньги? Отличный повод пополнить копилку.",
    "Перед покупкой проверь, сколько у тебя есть, — тогда хватит и на то, что важнее.",
  ],
  uz: [
    "Avval jamg'ar, keyin sarfla: cho'ntak puli kelishi bilan bir qismini jamg'armaga sol.",
    "Biror narsa olging keldimi? Bir kun kut. Hali ham xohlasang — demak, rostdan kerak.",
    "Xarajatlaringni yozib bor — shunda pul qayerga ketayotgani ko'rinadi.",
    "Mayda xaridlar qo'shilib boradi: har kuni 5 000 — oyiga 150 000 degani.",
    "Maqsad aniq bo'lsa, yig'ish oson: nima uchun yig'ayotganingni va u qancha turishini bil.",
    "Narxlarni solishtir: boshqa do'konda xuddi shu narsa arzonroq bo'lishi mumkin.",
    "Kerakmi yoki xohishmi? Kerakli narsasiz bo'lmaydi, xohishni esa kutsa ham bo'ladi.",
    "Jamg'armadan olgan bo'lsang — qanday qaytarishni o'yla. Jamg'arma pul solinganda o'sadi.",
    "Pul sovg'a qilishdimi? Jamg'armani to'ldirish uchun ajoyib imkoniyat.",
    "Xariddan oldin qancha puling borligini tekshir — shunda muhimroq narsaga ham yetadi.",
  ],
};

/** Совет недели для даты YYYY-MM-DD: меняется по понедельникам, один и тот же в боте и в приложении. */
export function tipOfWeek(date: string, lang: Language): string {
  const tips = POCKET_TIPS[lang];
  // 1970-01-01 — четверг: +3 дня сдвигают границу недели на понедельник.
  const week = Math.floor((Date.parse(`${date}T00:00:00Z`) / 86_400_000 + 3) / 7);
  return tips[((week % tips.length) + tips.length) % tips.length]!;
}
